/**
 * Phase 11 — Invoice Status State Machine
 *
 * Canonical transition table (must match DB trigger
 * `customer_invoice_status_guard` exactly):
 *
 *   estimate    → authorized, void
 *   authorized  → finalized, void
 *   finalized   → paid, void
 *   paid        → finalized   (refund-driven only — server-controlled)
 *   void        → (terminal)
 *
 * Used to gate UI buttons. Never trust the client; the DB trigger is
 * the ultimate authority.
 */

export type InvoiceStatus = 'estimate' | 'authorized' | 'finalized' | 'paid' | 'void';

export const INVOICE_STATUS_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  estimate: ['authorized', 'void'],
  authorized: ['finalized', 'void'],
  finalized: ['paid', 'void'],
  // `paid → finalized` is server-only via the refund path; never UI-initiated.
  paid: [],
  void: [],
};

export function canTransition(from: InvoiceStatus, to: InvoiceStatus): boolean {
  return INVOICE_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export function canVoid(status: InvoiceStatus): boolean {
  return status === 'estimate' || status === 'authorized' || status === 'finalized';
}

export function canEditLines(status: InvoiceStatus): boolean {
  return status === 'estimate';
}

export function canRecordPayment(status: InvoiceStatus): boolean {
  // Payments only legal after authorization; refunds also use this path.
  return status === 'authorized' || status === 'finalized' || status === 'paid';
}

export function canFinalize(status: InvoiceStatus, hasPreAuth: boolean): boolean {
  return status === 'authorized' && hasPreAuth;
}

export function canRequestPreAuth(status: InvoiceStatus): boolean {
  return status === 'estimate';
}

export function canRequestPostAuth(status: InvoiceStatus): boolean {
  return status === 'finalized' || status === 'paid';
}

export function isTerminal(status: InvoiceStatus): boolean {
  return status === 'void' || status === 'paid';
}

export function statusLabel(status: InvoiceStatus): string {
  switch (status) {
    case 'estimate': return 'Estimate';
    case 'authorized': return 'Authorized';
    case 'finalized': return 'Finalized';
    case 'paid': return 'Paid';
    case 'void': return 'Void';
    default: return status;
  }
}
